import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, Phone, MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CRM_WORDMARK } from "@/lib/brand";
import { useSite, itemHref } from "@/context/SiteContext";
import { cmsText } from "@/lib/cms";

const Kolom = ({ titel, children, testId }) => (
  <div data-testid={testId}>
    <h3 className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-ink-foreground/45">{titel}</h3>
    <ul className="mt-4 space-y-2.5 text-sm">{children}</ul>
  </div>
);

const FooterLink = ({ to, children, testId }) => (
  <li>
    <Link className="group inline-flex items-center gap-1 text-ink-foreground/75 transition-colors hover:text-ink-foreground" to={to} data-testid={testId}>
      {children}
      <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-60" aria-hidden="true" />
    </Link>
  </li>
);

export const Footer = () => {
  const { t, i18n } = useTranslation();
  const { site, menus } = useSite();
  const { isAuthenticated } = useAuth();
  const lang = i18n.language;
  const footerMenus = (site?.footer_menus || []).map((id) => menus.find((m) => m.id === id)).filter(Boolean);
  const c = site?.contact || {};
  const telDigits = (v) => String(v || "").replace(/[^\d+]/g, "");
  const whatsapp = telDigits(c.whatsapp || c.telefoon).replace(/^\+/, "").replace(/^00/, "");
  const waUrl = whatsapp ? `https://wa.me/${whatsapp}?text=${encodeURIComponent(t("footer.whatsappText"))}` : null;

  return (
    <footer className="section-ink relative mt-auto overflow-hidden grain" data-testid="site-footer">
      <div className="absolute -right-32 -top-48 h-[420px] w-[420px] rounded-full bg-primary-light/10 blur-3xl" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-20">
        <div className="lg:col-span-5">
          <img src={site?.logo_url || CRM_WORDMARK} alt={t("brand.name")} className="h-7 w-auto brightness-0 invert" data-testid="footer-wordmark" />
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-ink-foreground/70" data-testid="footer-tekst">
            {cmsText(site?.footer_tekst, lang) || t("footer.tagline")}
          </p>
          {(c.email || c.telefoon || c.adres) && (
            <div className="mt-5 space-y-1 font-mono text-xs text-ink-foreground/55" data-testid="footer-contact">
              {c.email && <p><a href={`mailto:${c.email}`} className="transition-colors hover:text-ink-foreground" data-testid="footer-contact-email">{c.email}</a></p>}
              {c.telefoon && <p><a href={`tel:${telDigits(c.telefoon)}`} className="transition-colors hover:text-ink-foreground" data-testid="footer-contact-telefoon">{c.telefoon}</a></p>}
              {c.adres && <p>{c.adres}</p>}
            </div>
          )}
          {(c.telefoon || waUrl) && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="footer-contact-acties">
              {c.telefoon && (
                <a href={`tel:${telDigits(c.telefoon)}`} className="inline-flex items-center gap-2 rounded-full border border-ink-foreground/20 px-4 py-2 text-xs font-medium text-ink-foreground/85 transition-colors hover:bg-ink-foreground/10" data-testid="footer-call-button">
                  <Phone className="h-3.5 w-3.5" aria-hidden="true" />{t("footer.call")}
                </a>
              )}
              {waUrl && (
                <a href={waUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-xs font-semibold text-[#0b3d22] transition-colors hover:bg-[#1fbe5a]" data-testid="footer-whatsapp-button">
                  <MessageCircle className="h-3.5 w-3.5" aria-hidden="true" />{t("footer.whatsapp")}
                </a>
              )}
            </div>
          )}
        </div>

        <div className="grid gap-10 sm:grid-cols-3 lg:col-span-7">
          {footerMenus.map((m) => (
            <Kolom key={m.id} titel={m.naam} testId={`footer-menu-${m.id}`}>
              {(m.items || []).map((item, i) => (
                <FooterLink key={i} to={itemHref(item)}>{cmsText(item.label, lang)}</FooterLink>
              ))}
            </Kolom>
          ))}
          {(site?.footer_kolommen || []).map((k, i) => (
            <div key={`kol-${i}`} data-testid="footer-kolom">
              <h3 className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-ink-foreground/45">{cmsText(k.titel, lang)}</h3>
              <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-ink-foreground/70">{cmsText(k.tekst, lang)}</p>
            </div>
          ))}
          <Kolom titel={t("footer.shop")}>
            {isAuthenticated ? (
              <>
                <FooterLink to="/producten" testId="footer-link-producten">{t("nav.producten")}</FooterLink>
                <FooterLink to="/mijn-bestellingen" testId="footer-link-bestellingen">{t("nav.bestellingen")}</FooterLink>
                <FooterLink to="/account" testId="footer-link-account">{t("nav.account")}</FooterLink>
              </>
            ) : (
              <>
                <FooterLink to="/assortiment" testId="footer-link-assortiment">{t("nav.assortiment")}</FooterLink>
                <FooterLink to="/login" testId="footer-link-login">{t("nav.login")}</FooterLink>
                <FooterLink to="/registreren" testId="footer-link-register">{t("nav.register")}</FooterLink>
              </>
            )}
          </Kolom>
        </div>
      </div>
      <div className="relative border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-5 font-mono text-[11px] text-ink-foreground/45 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <span>{t("footer.rights", { year: new Date().getFullYear() })}</span>
          <span>{t("footer.pricesNotice")}</span>
        </div>
      </div>
    </footer>
  );
};
